import React from "react";
import { useTranslation } from "react-i18next";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { getCsvErrorByCode as getCsvErrorByCodeJa } from "../locale/ja/csv-error-codes";
import { getCsvErrorByCode as getCsvErrorByCodeEn } from "../locale/en/csv-error-codes";

interface ErrorModalProps {
  isOpen: boolean;
  onClose: () => void;
  errorCode?: string;
  errorMessage?: string;
  fileName?: string;
}

export function ErrorModal({
  isOpen,
  onClose,
  errorCode,
  errorMessage,
  fileName,
}: ErrorModalProps) {
  const { t, i18n } = useTranslation();

  const getCsvErrorByCode = i18n.language === 'en' ? getCsvErrorByCodeEn : getCsvErrorByCodeJa;
  const csvError = errorCode ? getCsvErrorByCode(errorCode) : null;
  const displayMessage = csvError?.message || errorMessage || t("data-mgmt-unknown-error");
  
  const extractErrorMessage = (description: string) => {
    const match = description.match(/Error in _store_timeseries: (.+?)(?: \(HTTP STATUS:|$)/);
    if (match) {
      return match[1].replace(/, at position \d+$/, '');
    }
    return description;
  };
  
  const displayDescription = (errorCode === "U7-100" && errorMessage) ? extractErrorMessage(errorMessage) : csvError?.description;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <DialogTitle className="text-left">
              {t("data-mgmt-error-modal-title")}
            </DialogTitle>
          </div>
          {fileName && (
            <div className="text-sm text-muted-foreground mb-3">
              {t("data-mgmt-error-modal-file-label")} {fileName}
            </div>
          )}
          <DialogDescription className="text-left text-base">
            {displayMessage}
          </DialogDescription>
          {displayDescription && (
            <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-md">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                {displayDescription}
              </p>
            </div>
          )}
          {errorCode && (
            <div className="mt-3 text-xs text-muted-foreground">
              {t("data-mgmt-error-modal-error-code-label")} {errorCode}
            </div>
          )}
        </DialogHeader>
      </DialogContent>
    </Dialog>
  );
}